import { getPageConfig } from '@/lib/aggregation';
import { NEW_CATEGORY_UIDS } from '@/app/(pages)/(category)/c/[slug]/constants';
import { getIsSiteWideJackpotEnabled } from '@/logic/page-config/static-page/static-page-utils';
import type {
    NavigationItem,
    NavigationItemExternalLink,
    NavigationItemLink,
    NavigationItemLinkType
} from '@/types/aggregation/common';
import { ROUTES } from '@/utils/constants';
import { removeProductFromUid } from '@/utils/url/url';
import { NAVIGATION_ITEM_TYPE_LIVESTREAM } from '@/utils/constants/navigation';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';

export type NavigationItemUI = {
    name: string;
    pathname: string | null;
    customImage: {
        name: string;
        url: string;
    };
    customImageActive: {
        name: string;
        url: string;
    };
    type?: string;
    linkUID?: string;
    linkType?: NavigationItemLinkType;
    isHighlighted?: boolean;
    newItemsCount?: number;
    pinned: boolean;
    isPersonalized: boolean;
};

export const getPathFromLink = (link: NavigationItemLink) => {
    switch (link.type) {
        case 'static_page':
            return `${ROUTES.STATIC_PAGE}${removeProductFromUid(link.uid)}`;
        case 'category':
            return `${ROUTES.CATEGORY}${removeProductFromUid(link.uid)}`;
        case 'homepage':
            return ROUTES.HOMEPAGE;
        case 'game':
        case 'game_lobby':
            return `${ROUTES.GAME}${link.uid}`;
        case 'dynamic_route':
            return `${ROUTES.DYNAMIC_PAGE}${removeProductFromUid(link.uid)}`;
        default:
            return null;
    }
};

export const getPathFromExternalLink = (external_link: NavigationItemExternalLink) => {
    switch (external_link.link_type) {
        case 'Document':
            return getPathFromLink(external_link);
        case 'Web':
            return external_link.url;
        case 'Media':
            return external_link.url;
        default:
            return null;
    }
};

export const getPathFromNavigationItem = (item: NavigationItem) => {
    if (item.item_type?.toLowerCase() === NAVIGATION_ITEM_TYPE_LIVESTREAM) {
        return ROUTES.LIVESTREAM;
    }

    const pathName = getPathFromLink(item.link);
    return pathName || getPathFromExternalLink(item.external_link);
};

const mapNavigationItem = (items: NavigationItem[], isPersonalizedNav: boolean): NavigationItemUI[] =>
    items
        .map((item: NavigationItem) => ({
            name: item.name,
            pathname: getPathFromNavigationItem(item),
            linkUID: item?.link?.uid,
            linkType: item?.link?.type,
            isHighlighted: item?.is_highlighted,
            type: item?.item_type,
            customImage: {
                name: item.custom_image.name,
                url: item.custom_image.url
            },
            customImageActive: {
                name: item.custom_image_active.name,
                url: item.custom_image_active.url
            },
            newItemsCount: item.new_items_count,
            pinned: !!item.pinned,
            isPersonalized: isPersonalizedNav
        }))
        .filter((link) => link.pathname)
        .filter((link, index, self) => self.findIndex((l) => l.pathname === link.pathname) === index);

/**
 * When jackpot is disabled, filters out SWJ categories from top nav.
 */
const filterSwjCategoryFromTopNav = (
    items: NavigationItemUI[],
    jackpotCategoryUids: Set<string>
): NavigationItemUI[] => {
    if (jackpotCategoryUids.size === 0) return items;

    return items.filter((item) => {
        if (item.linkType !== 'category' || !item.linkUID) return true;
        const categorySlug = removeProductFromUid(item.linkUID);
        return !jackpotCategoryUids.has(categorySlug);
    });
};

const filterLivestreamFromTopNav = (items: NavigationItemUI[], hasLivestream: boolean) => {
    if (hasLivestream) {
        return items;
    }

    return items.filter((item) => item.type?.toLowerCase() !== NAVIGATION_ITEM_TYPE_LIVESTREAM);
};

const removeTopNavItemsDuplicatedInBottomBar = (
    items: NavigationItemUI[],
    bottomBarItems: NavigationItem[]
): NavigationItemUI[] =>
    items.filter((item) => {
        const uid = item.linkUID ?? '';

        return (
            NEW_CATEGORY_UIDS.includes(uid) ||
            !bottomBarItems.some(
                (bottomItem) =>
                    bottomItem.link.uid === uid ||
                    (item.pathname !== null && getPathFromNavigationItem(bottomItem) === item.pathname)
            )
        );
    });

export const getTopNav = async ({ currentRoute }: { currentRoute: string }): Promise<NavigationItemUI[]> => {
    const json = await getPageConfig(currentRoute);

    if (!json?.pageConfig?.product?.data?.top_navigation?.data?.items?.length) {
        return [];
    }

    const bottomBarItems = json.pageConfig.product.data.bottom_bar?.data.items ?? [];
    const hasPersonalizedRankings = (json.pageConfig.categoryRankings?.categories?.length ?? 0) > 0;
    const items = mapNavigationItem(json.pageConfig.product.data.top_navigation.data.items, hasPersonalizedRankings);

    const hasLivestream =
        json.pageConfig.product.data.features?.some(
            (feature) => feature.feature_name === PRODUCT_THROTTLES.HAS_LIVESTREAM
        ) ?? false;
    const visibleItems = filterLivestreamFromTopNav(
        removeTopNavItemsDuplicatedInBottomBar(items, bottomBarItems),
        hasLivestream
    );

    if (!getIsSiteWideJackpotEnabled(json.pageConfig.product)) {
        const jackpotCategories = json.pageConfig.product.data?.site_wide_jackpot?.jackpot_categories ?? [];
        const jackpotCategoryUids = new Set(
            jackpotCategories
                .map((item: { category?: { uid?: string }; uid?: string }) => item.category?.uid ?? item.uid)
                .filter((uid): uid is string => Boolean(uid))
                .map((uid) => removeProductFromUid(uid))
        );
        return filterSwjCategoryFromTopNav(visibleItems, jackpotCategoryUids);
    }

    return visibleItems;
};

export const getBottomNav = async ({ currentRoute }: { currentRoute: string }): Promise<NavigationItemUI[] | null> => {
    const json = await getPageConfig(currentRoute);

    if (!json?.pageConfig?.product?.data?.bottom_bar?.data?.items) {
        return null;
    }

    return mapNavigationItem(json.pageConfig.product.data.bottom_bar.data.items, false);
};
